import { Eye, ThumbsDown, ThumbsUp } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { ErrorState } from '@nucleus-commerce/admin-kit/errors';
import { FormSection } from '@nucleus-commerce/admin-kit/forms';
import type { HelpArticle, HelpArticleFeedback } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Badge } from '@nucleus-commerce/ui/components/badge';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';

import { useHelpArticleFeedback } from '../hooks/use-help-article-feedback';

function Stat({ icon: Icon, label, value }: { icon: typeof Eye; label: string; value: string }) {
  return (
    <div className="bg-muted/40 flex flex-col gap-1 rounded-md border px-3 py-2">
      <span className="text-muted-foreground flex items-center gap-1.5 text-xs">
        <Icon className="size-3.5" aria-hidden />
        {label}
      </span>
      <span className="text-lg font-semibold tabular-nums">{value}</span>
    </div>
  );
}

function FeedbackRow({ vote }: { vote: HelpArticleFeedback }) {
  const { t } = useTranslation('helpCenter');
  const format = useFormat();
  const Icon = vote.helpful ? ThumbsUp : ThumbsDown;
  return (
    <li className="flex items-start gap-3 py-3">
      <Icon
        className={vote.helpful ? 'text-success mt-0.5 size-4' : 'text-destructive mt-0.5 size-4'}
        aria-label={vote.helpful ? t('feedback.helpful') : t('feedback.notHelpful')}
      />
      <div className="min-w-0 flex-1">
        {vote.comment ? (
          <p className="text-sm whitespace-pre-line">{vote.comment}</p>
        ) : (
          <p className="text-muted-foreground text-sm">{t('feedback.noComment')}</p>
        )}
        <p className="text-muted-foreground mt-1 flex items-center gap-2 text-xs">
          <span title={format.utc(vote.createdAt)}>{format.relative(vote.createdAt)}</span>
          <Badge variant="outline" className="font-normal">
            {t(`feedback.source.${vote.source}`)}
          </Badge>
        </p>
      </div>
    </li>
  );
}

/** Helpful counts, views and the latest votes (with their comments) of a saved article. */
export function HelpArticleFeedbackSection({
  storeId,
  article,
}: {
  storeId: string;
  article: HelpArticle;
}) {
  const { t } = useTranslation('helpCenter');
  const format = useFormat();
  const query = useHelpArticleFeedback(storeId, article.id);
  const votes = query.data?.pages.flatMap((page) => page.items) ?? [];
  const total = article.helpfulYes + article.helpfulNo;

  return (
    <FormSection title={t('feedback.title')} description={t('feedback.description')}>
      <div className="grid grid-cols-3 gap-3">
        <Stat icon={ThumbsUp} label={t('feedback.yes')} value={format.number(article.helpfulYes)} />
        <Stat icon={ThumbsDown} label={t('feedback.no')} value={format.number(article.helpfulNo)} />
        <Stat icon={Eye} label={t('feedback.views')} value={format.number(article.views)} />
      </div>
      {total > 0 ? (
        <p className="text-muted-foreground text-xs">
          {t('feedback.ratio', {
            percent: format.number(article.helpfulYes / total, { style: 'percent' }),
          })}
        </p>
      ) : null}
      {query.isPending ? (
        <div className="flex flex-col gap-2" aria-busy="true">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      ) : query.isError ? (
        <ErrorState compact error={query.error} onRetry={() => void query.refetch()} />
      ) : votes.length === 0 ? (
        <p className="text-muted-foreground text-sm">{t('feedback.empty')}</p>
      ) : (
        <>
          <ul className="divide-y" aria-label={t('feedback.listLabel')}>
            {votes.map((vote) => (
              <FeedbackRow key={vote.id} vote={vote} />
            ))}
          </ul>
          {query.hasNextPage ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="self-start"
              disabled={query.isFetchingNextPage}
              onClick={() => void query.fetchNextPage()}
            >
              {query.isFetchingNextPage ? t('feedback.loading') : t('feedback.loadMore')}
            </Button>
          ) : null}
        </>
      )}
    </FormSection>
  );
}
